import { useEffect, useRef, useState } from 'react';
import {
  AsyncThunk, Dispatch, PayloadAction, unwrapResult,
} from '@reduxjs/toolkit';
import { useAppDispatch } from '../../../store/hooks';
import { useIsMobile } from '../../../common/hooks/useResponsive';
import { GetContentRequestV4 } from '../../../common/models/content/GetContentRequest';
import { Content, FeedInteractionAction } from '../../../common/models/content/Content';
import { RootState } from '../../../store/storeSetup';
import { ResponseError } from '../../../store/thunks/contentThunk';
import { useScrollRestoration } from '../../../common/hooks/useScrollRestoration';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { Logger } from '../../../config/logging/logger';
import { getRandomInteger } from '../../../common/RandomNumberHelper';
// import { UpdateHasMoreContent, UpdatePageNumber } from '../../../../store/slices/contentSlice';

interface useFetchCategoryContentprops {
  viewMorePage?: boolean;
  contentRequestOptions: GetContentRequestV4
  getCategoryContent: AsyncThunk<Content[], GetContentRequestV4, {
    rejectValue: ResponseError;
    state: RootState;
    dispatch?: Dispatch | undefined;
    extra?: unknown;
    serializedErrorType?: unknown;
    pendingMeta?: unknown;
    fulfilledMeta?: unknown;
    rejectedMeta?: unknown;
  }>;
  resetCategoryContentList: (payload: Content[]) => PayloadAction<Content[]>;
  setSomeContentLoadedInitially: (value: boolean) => void;
  setOpenErrorSnackbar: (value: boolean) => void;
  setInitialFetchDone: (value: boolean) => void;
  updateCategoryPageNumber: (payload: number) => PayloadAction<number>,
  updateHasMoreCategoryContent: (payload: boolean) => PayloadAction<boolean>,
}
export const useFetchCategoryContent = ({
  viewMorePage = false,
  contentRequestOptions,
  getCategoryContent,
  resetCategoryContentList,
  setSomeContentLoadedInitially,
  setOpenErrorSnackbar,
  setInitialFetchDone,
  updateCategoryPageNumber,
  updateHasMoreCategoryContent,
}: useFetchCategoryContentprops) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const contentFetchInitiated = useRef(false);
  const { restoreScroll } = useScrollRestoration();
  const { trackFeedInteraction } = useCustomEventTracking();
  const moreContentPageSize = isMobile ? 5 : 15;
  const initPageSize = viewMorePage ? moreContentPageSize : 3;
  const logger = Logger.create('useFetchCategoryContent');

  // Generate a random seed once per component mount to ensure consistent ordering across pagination
  const [randomSeed] = useState(() => getRandomInteger(1, 10000));

  useEffect(() => {
    if (restoreScroll()) {
      return;
    }

    if (!contentFetchInitiated.current) {
      contentFetchInitiated.current = true;
      dispatch(resetCategoryContentList([])); // initial category content list state
      dispatch(getCategoryContent({
        ...contentRequestOptions,
        pageSize: initPageSize,
        page: 1,
        randomSeed,
      }))
        .then(unwrapResult)
        .then((res) => {
          setOpenErrorSnackbar(false);
          setSomeContentLoadedInitially(true);
          setInitialFetchDone(true);
          // Move to page to 2 so infinite scroll can begin from there
          dispatch(updateCategoryPageNumber(2));
          if (res.length === initPageSize) {
            dispatch(updateHasMoreCategoryContent(true));
          } else {
            dispatch(updateHasMoreCategoryContent(false));
          }
        })
        .catch((err) => {
          setOpenErrorSnackbar(true);
          logger.error(Logger.format(err));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMoreCategoryContent = (
    // manage in redux store
    categoryPage: number,
    hasMoreCategoryContent: boolean,
  ) => {
    if (!hasMoreCategoryContent) return;

    if (categoryPage > 1) {
      trackFeedInteraction(
        'Load',
        'MoreContent',
        {
          action: FeedInteractionAction.ReachEnd,
        },
      );
    }

    dispatch(getCategoryContent({
      ...contentRequestOptions,
      pageSize: moreContentPageSize,
      page: categoryPage,
      randomSeed,
    })).then((response) => {
      setOpenErrorSnackbar(false);
      const res = response.payload as Content[];
      // our currrent BE ensures that we never get more content in the response than pageSize
      dispatch(updateHasMoreCategoryContent(res.length === moreContentPageSize));
      if (hasMoreCategoryContent) {
        dispatch(updateCategoryPageNumber(categoryPage + 1));
      }
    }).catch((e) => {
      setOpenErrorSnackbar(true);
      logger.error(Logger.format(e));
    });
  };

  return fetchMoreCategoryContent;
};
